import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Bot, Plus, Minus, Check, Share2 } from "lucide-react";
import { apiClient } from "../../services/api";
import VideoPlayer from "../../components/workout/VideoPlayer";
import PersonalAITrainer from "../../components/personal-ai-trainer/PersonalAITrainer";
import PlacementGuide, { GUIDE_FLAG } from "../../components/personal-ai-trainer/PlacementGuide";
import useTtsCoach from "../../hooks/useTtsCoach";
import { useAuthStore } from "../../store/authStore";
import MarketplaceStrip from "../../components/marketplace/MarketplaceStrip";
import WorkoutCardShareSheet from "../../components/community/WorkoutCardShareSheet";
import { toast } from "sonner";

const SHARE_SKIP_KEY = "fitfrik_workoutcard_skip_count";

// Try to pull the leading numeric part of a reps string (e.g. "30", "8-12", "30-45s") — used to seed the set tracker.
function parseRepsSeed(reps) {
  if (typeof reps === "number") return reps;
  const s = String(reps || "").match(/\d+/);
  return s ? parseInt(s[0], 10) : 10;
}

export default function LiveSession() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);

  const [plan, setPlan] = useState(null);
  const [exIdx, setExIdx] = useState(0);
  const [setIdx, setSetIdx] = useState(0);
  const [phase, setPhase] = useState("active"); // active | rest | done
  const [secs, setSecs] = useState(0);
  const [paused, setPaused] = useState(false);
  // Personal AI Trainer — default ON for home module, OFF for gym/yoga/zumba
  const [aiTrainerOn, setAiTrainerOn] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [adjustment, setAdjustment] = useState(null);
  // Set-tracker state per-exercise: [{weight, reps, done}, ...]
  const [setLogs, setSetLogs] = useState([]);
  // Mirror toggle — flips the exercise media (and PAT preview) horizontally
  const [mirrorOn, setMirrorOn] = useState(true);
  const [muted, setMuted] = useState(false);
  const patRef = useRef(null);
  const aiTelemetryRef = useRef({}); // exercise_name → tracked aggregate
  const sessionStartRef = useRef(Date.now());
  const tickRef = useRef(null);
  const tts = useTtsCoach();
  const announcedExerciseRef = useRef(null);
  const announcedDoneRef = useRef(false);

  useEffect(() => {
    apiClient
      .get(`/workout/plans/${planId}`)
      .then((r) => {
        setPlan(r.data);
        const mod = (r.data?.module || "").toLowerCase();
        setAiTrainerOn(mod === "home");
      })
      .catch(() => {
        toast.error("Plan not found");
        navigate("/workouts/gym");
      });
    // Fetch active programme adjustment from AI Coach (Phase 2 Part B)
    apiClient
      .get("/ai-trainer/adjustment/active")
      .then((r) => {
        setAdjustment(r.data?.adjustment || null);
        if (r.data?.adjustment) {
          // Mark applied — fire-and-forget; this adjustment is now in effect for this session
          apiClient.post("/ai-trainer/adjustment/apply").catch(() => {});
        }
      })
      .catch(() => setAdjustment(null));
  }, [planId]);

  const exercises = plan?.main_sets || [];
  const current = exercises[exIdx];

  // Seed / reset set-tracker rows when the current exercise changes
  useEffect(() => {
    if (!current) {
      setSetLogs([]);
      return;
    }
    const n = current.sets || 3;
    const seed = parseRepsSeed(current.reps);
    setSetLogs(
      Array.from({ length: n }, () => ({ weight: 0, reps: seed, done: false }))
    );
  }, [current?.exercise_id, current?.sets, current?.reps]);

  // timer
  useEffect(() => {
    if (paused || phase === "done") return;
    tickRef.current = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(tickRef.current);
  }, [paused, phase]);

  const restTarget = current?.rest_seconds || 60;
  const ringPct =
    phase === "rest" ? Math.min(100, (secs / restTarget) * 100) : Math.min(100, ((secs % 30) / 30) * 100);

  // TTS — announce per exercise + on rest start + on completion
  useEffect(() => {
    if (!tts.enabled || !current) return;
    if (phase === "active" && announcedExerciseRef.current !== `${exIdx}-${setIdx}`) {
      announcedExerciseRef.current = `${exIdx}-${setIdx}`;
      // On exercise change (setIdx===0), say full intro; on subsequent sets just brief
      if (setIdx === 0) {
        tts.speak(`${current.name}. ${current.sets} sets of ${current.reps} reps.`);
      }
    }
  }, [phase, exIdx, setIdx, current, tts]);

  useEffect(() => {
    if (!tts.enabled) return;
    if (phase === "rest") {
      tts.speak(`Good set. Rest for ${current?.rest_seconds || 60} seconds.`);
    }
  }, [phase, current, tts]);

  useEffect(() => {
    if (!tts.enabled || phase !== "done" || announcedDoneRef.current) return;
    announcedDoneRef.current = true;
    tts.speak(
      `Workout complete. You burned ${plan?.estimated_calories || 240} calories and earned 50 FitPoints. Great work.`
    );
  }, [phase, plan, tts]);

  const completeSet = () => {
    if (!current) return;
    // Capture Personal AI Trainer telemetry for this set BEFORE state moves on
    if (aiTrainerOn && patRef.current) {
      const snapshot = patRef.current.finaliseSet?.();
      if (snapshot) {
        const k = current.name;
        const cur = aiTelemetryRef.current[k] || {
          exercise_name: k,
          exercise_category: current.muscle_group || null,
          sets_completed: 0,
          reps_per_set: [],
          partial_reps_per_set: [],
          hold_times_seconds: [],
          form_bar_green_pct_total: 0,
          form_bar_amber_pct_total: 0,
          form_bar_red_pct_total: 0,
          issues_detected: new Set(),
          suggestions_shown: new Set(),
        };
        cur.sets_completed += 1;
        cur.reps_per_set.push(snapshot.reps || 0);
        cur.partial_reps_per_set.push(snapshot.partialReps || 0);
        cur.hold_times_seconds.push(snapshot.holdSeconds || 0);
        cur.form_bar_green_pct_total += snapshot.formBarPct?.green || 0;
        cur.form_bar_amber_pct_total += snapshot.formBarPct?.amber || 0;
        cur.form_bar_red_pct_total += snapshot.formBarPct?.red || 0;
        (snapshot.issuesDetected || []).forEach((i) => cur.issues_detected.add(i));
        if (snapshot.suggestionShown) cur.suggestions_shown.add(snapshot.suggestionShown);
        aiTelemetryRef.current[k] = cur;
      }
    }

    if (setIdx + 1 < (current.sets || 1)) {
      setSetIdx((s) => s + 1);
      setPhase("rest");
      setSecs(0);
    } else {
      // next exercise
      if (exIdx + 1 < exercises.length) {
        setExIdx((i) => i + 1);
        setSetIdx(0);
        setPhase("active");
        setSecs(0);
      } else {
        finishSession();
      }
    }
  };

  const skipRest = () => {
    setPhase("active");
    setSecs(0);
  };

  const [prExercises, setPrExercises] = useState([]);
  const [shopStrip, setShopStrip] = useState(null);
  const [completedSession, setCompletedSession] = useState(null); // workout session for share card
  const [shareSheetOpen, setShareSheetOpen] = useState(false);

  const finishSession = async () => {
    let savedSession = null;
    try {
      const { data } = await apiClient.post("/workout/sessions", {
        plan_id: planId,
        exercises: exercises.map((e) => ({
          exercise_id: e.exercise_id,
          name: e.name,
          muscle_group: e.muscle_group,
          sets: e.sets,
          reps: e.reps,
          weight_kg: e.weight_kg || 0,
        })),
        duration_minutes: plan?.duration_minutes || 30,
        calories_burned: plan?.estimated_calories || 240,
      });
      setPrExercises(data?.pr_exercises || []);
      savedSession = data;
      if (data?.contains_pr) {
        toast.success("New personal best! · +50 FitPoints");
      } else {
        toast.success("Session logged · +50 FitPoints");
      }
    } catch {
      // still navigate even if log fails
    }

    // Post Personal AI Trainer session (if anything was tracked)
    const tracked = Object.values(aiTelemetryRef.current);
    if (aiTrainerOn && tracked.length > 0) {
      try {
        const exercises_tracked = tracked.map((t) => ({
          exercise_name: t.exercise_name,
          exercise_category: t.exercise_category,
          sets_completed: t.sets_completed,
          reps_per_set: t.reps_per_set,
          partial_reps_per_set: t.partial_reps_per_set,
          hold_times_seconds: t.hold_times_seconds,
          form_bar_green_pct: t.sets_completed
            ? Math.round(t.form_bar_green_pct_total / t.sets_completed)
            : 100,
          form_bar_amber_pct: t.sets_completed
            ? Math.round(t.form_bar_amber_pct_total / t.sets_completed)
            : 0,
          form_bar_red_pct: t.sets_completed
            ? Math.round(t.form_bar_red_pct_total / t.sets_completed)
            : 0,
          issues_detected: [...t.issues_detected],
          suggestions_shown: [...t.suggestions_shown],
        }));
        const active_seconds = Math.max(
          1,
          Math.round((Date.now() - sessionStartRef.current) / 1000)
        );
        const fpsMode = patRef.current?.fpsMode || "standard";
        const manualMode = ["permission_denied", "no_camera", "unsupported", "error"].includes(
          patRef.current?.status
        );
        const { data: aiResp } = await apiClient.post("/ai-trainer/sessions/end", {
          workout_session_id: planId,
          exercises_tracked,
          active_seconds,
          device_fps_mode: fpsMode,
          manual_mode: manualMode,
        });
        if (aiResp?.fitpoints_awarded > 0) {
          toast.success(`AI Trainer · +${aiResp.fitpoints_awarded} FitPoints`);
        }
      } catch {
        // never block workout completion on AI Trainer failure
      }
    }

    setPhase("done");
    // Build a session-shaped object for the share card (uses live data, not DB roundtrip)
    const cardSession = {
      session_id: savedSession?.session_id,
      workout_name: plan?.plan_name,
      module: plan?.module,
      duration_minutes: plan?.duration_minutes || 30,
      total_sets: exercises.reduce((s, e) => s + (e.sets || 0), 0),
      total_reps: exercises.reduce((s, e) => s + (e.sets || 0) * (e.reps || 0), 0),
      ai_trainer_active: aiTrainerOn,
      form_score: savedSession?.form_score,
      completed_at: new Date().toISOString(),
    };
    setCompletedSession(cardSession);
    // Auto-open share sheet unless user has skipped 3 times in a row
    let skips = 0;
    try { skips = parseInt(localStorage.getItem(SHARE_SKIP_KEY) || "0", 10) || 0; } catch { /* ignore */ }
    if (skips < 3 && cardSession.session_id) {
      // Small delay so completion animation finishes first
      setTimeout(() => setShareSheetOpen(true), 800);
    }
    // Fetch context-aware product strip for this module
    const mod = plan?.module || "gym";
    apiClient
      .get(`/marketplace/context/workout-complete?module=${mod}&limit=3`)
      .then((r) => setShopStrip(r.data))
      .catch(() => setShopStrip(null));
  };

  // auto-advance rest
  useEffect(() => {
    if (phase === "rest" && secs >= restTarget) {
      setPhase("active");
      setSecs(0);
    }
  }, [phase, secs, restTarget]);

  if (!plan) {
    return (
      <div className="min-h-screen grid place-items-center bg-canvas">
        <div className="text-ink-muted">Loading session…</div>
      </div>
    );
  }

  if (phase === "done") {
    return (
      <div className="min-h-screen bg-canvas px-6 py-8 overflow-y-auto" data-testid="session-complete">
        <div className="max-w-md mx-auto text-center bg-white rounded-[16px] p-8" style={{ boxShadow: "var(--shadow-card)" }}>
          <div className="w-16 h-16 mx-auto rounded-full grid place-items-center" style={{ background: "var(--accent-green-dim)" }}>
            <span className="font-display text-[28px]" style={{ color: "var(--accent-green)" }}>✓</span>
          </div>
          <h2 className="font-display text-[28px] mt-4">Session complete</h2>
          <p className="text-[14px] text-ink-soft mt-2">
            {plan.plan_name} · {exercises.length} exercises · ~{plan.estimated_calories} kcal
          </p>
          {prExercises.length > 0 && (
            <div
              className="mt-4 px-4 py-3 mx-auto inline-flex items-center gap-2"
              data-testid="pr-celebration"
              style={{
                background: "rgba(0, 212, 255, 0.14)",
                border: "1px solid #00D4FF",
                borderRadius: 14,
                fontFamily: "Manrope",
                fontSize: 14,
                fontWeight: 700,
                color: "#1A1A1A",
                lineHeight: 1.3,
              }}
            >
              New personal best on {prExercises.slice(0, 2).join(", ")}
              {prExercises.length > 2 ? ` +${prExercises.length - 2} more` : ""}
            </div>
          )}
          <div className="mt-6 flex gap-3">
            <button
              onClick={() => navigate("/home")}
              className="flex-1 h-12 rounded-pill border border-line bg-white"
              style={{ borderRadius: 100 }}
            >
              Home
            </button>
            <button
              onClick={() => navigate("/health")}
              data-testid="see-progress"
              className="flex-1 h-12 rounded-pill"
              style={{ background: "var(--accent-blue)", color: "#000", borderRadius: 100, fontWeight: 600 }}
            >
              See Progress
            </button>
          </div>
          {completedSession?.session_id && (
            <button
              onClick={() => setShareSheetOpen(true)}
              data-testid="share-workout-btn"
              className="mt-3 w-full h-11 rounded-pill"
              style={{
                background: "transparent",
                color: "var(--text-primary, #0C0C0B)",
                border: "1px solid var(--border, rgba(12,12,11,0.16))",
                borderRadius: 100,
                fontFamily: "Barlow", fontSize: 13, fontWeight: 600,
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
            >
              <Share2 size={14} /> Share Workout
            </button>
          )}
        </div>

        {/* Context-aware product strip */}
        {shopStrip && shopStrip.products?.length > 0 && (
          <div className="max-w-md mx-auto mt-6">
            <MarketplaceStrip
              headline={shopStrip.headline}
              sub={shopStrip.sub}
              products={shopStrip.products}
              shopHref={`/marketplace?category=${shopStrip.category}`}
              testid="workout-complete-shop-strip"
            />
          </div>
        )}

        <AnimatePresence>
          {shareSheetOpen && completedSession?.session_id && (
            <WorkoutCardShareSheet
              session={completedSession}
              onClose={() => setShareSheetOpen(false)}
              onPosted={() => {
                setShareSheetOpen(false);
                try { localStorage.setItem(SHARE_SKIP_KEY, "0"); } catch { /* ignore */ }
              }}
              onSkip={() => {
                try {
                  const cur = parseInt(localStorage.getItem(SHARE_SKIP_KEY) || "0", 10) || 0;
                  localStorage.setItem(SHARE_SKIP_KEY, String(cur + 1));
                } catch { /* ignore */ }
              }}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Set-tracker mutators
  const nudgeSet = (i, key, delta) => {
    setSetLogs((rows) =>
      rows.map((r, idx) =>
        idx === i ? { ...r, [key]: Math.max(0, (r[key] || 0) + delta) } : r
      )
    );
  };
  const toggleSetDone = (i) => {
    setSetLogs((rows) => rows.map((r, idx) => (idx === i ? { ...r, done: !r.done } : r)));
    // Also call the legacy completeSet flow so telemetry stays aligned
    if (!setLogs[i]?.done) completeSet();
  };

  const goPrev = () => {
    if (exIdx === 0) return;
    setExIdx((i) => i - 1);
    setSetIdx(0);
    setPhase("active");
    setSecs(0);
  };
  const goNextExercise = () => {
    if (exIdx + 1 < exercises.length) {
      setExIdx((i) => i + 1);
      setSetIdx(0);
      setPhase("active");
      setSecs(0);
    } else {
      finishSession();
    }
  };

  const heroThumb =
    current?.thumbnail_url ||
    "https://images.unsplash.com/photo-1517963879433-6ad2b056d712?w=1200&q=85&auto=format";

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "#0B0B0B", color: "#F4F3F1" }}
      data-testid="live-session"
    >
      {/* Top bar */}
      <header
        className="flex items-center justify-between px-3"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 8px)", paddingBottom: 8 }}
      >
        <button
          onClick={() => navigate(-1)}
          aria-label="Back"
          data-testid="session-back"
          className="w-10 h-10 grid place-items-center rounded-full"
          style={{ background: "rgba(255,255,255,0.08)", border: 0, cursor: "pointer" }}
        >
          <ChevronLeft size={20} color="#F4F3F1" />
        </button>
        <div style={{ textAlign: "center", flex: 1, minWidth: 0, padding: "0 8px" }}>
          <div
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 700,
              fontSize: 18,
              color: "#F4F3F1",
              lineHeight: 1.15,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            data-testid="session-title"
          >
            {plan.plan_name}
          </div>
          <div
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 12,
              color: "rgba(244,243,241,0.55)",
              marginTop: 2,
            }}
            data-testid="session-progress"
          >
            Exercise {exIdx + 1} of {exercises.length}
          </div>
        </div>
        <button
          onClick={finishSession}
          data-testid="end-session"
          style={{
            padding: "8px 18px",
            borderRadius: 100,
            background: "transparent",
            border: "1px solid #E5484D",
            color: "#E5484D",
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 600,
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          End
        </button>
      </header>

      <div className="max-w-2xl mx-auto w-full" style={{ padding: "12px 16px 24px" }}>
        {/* Exercise media */}
        <div
          style={{
            position: "relative",
            borderRadius: 20,
            overflow: "hidden",
            background: "#111",
            border: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div style={{ transform: mirrorOn ? "scaleX(-1)" : "none", transition: "transform 240ms ease" }}>
            <VideoPlayer
              exerciseName={current?.name}
              s3Url={current?.s3_video_url || current?.video_url}
              s3MobileUrl={current?.s3_video_mobile_url}
              ytId={current?.youtube_id}
              thumbnail={heroThumb}
              nextS3Url={exercises[exIdx + 1]?.s3_video_url}
              nextS3MobileUrl={exercises[exIdx + 1]?.s3_video_mobile_url}
              muted={muted}
            />
          </div>
          <div
            style={{
              position: "absolute",
              bottom: 12,
              left: 16,
              right: 60,
              color: "#fff",
              pointerEvents: "none",
              textShadow: "0 2px 8px rgba(0,0,0,0.65)",
            }}
          >
            <div
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 800,
                fontSize: 28,
                lineHeight: 1,
              }}
              data-testid="current-ex-name"
            >
              {current?.name}
            </div>
            <div
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 12,
                color: "rgba(255,255,255,0.85)",
                marginTop: 6,
              }}
              data-testid="current-ex-target"
            >
              Target: {current?.sets || 3} × {current?.reps || "8-12"} · rest {current?.rest_seconds || 60}s
            </div>
          </div>
          <button
            onClick={() => setMuted((m) => !m)}
            data-testid="session-mute-toggle"
            aria-label={muted ? "Unmute" : "Mute"}
            style={{
              position: "absolute",
              bottom: 12,
              right: 12,
              width: 36,
              height: 36,
              borderRadius: 100,
              background: "rgba(0,0,0,0.55)",
              border: 0,
              color: "#F4F3F1",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
            }}
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
        </div>

        {/* AI Trainer adjustment note — kept intact */}
        {adjustment?.focus_exercise &&
          current?.name &&
          adjustment.focus_exercise.toLowerCase().includes(current.name.toLowerCase().split(" ")[0]) && (
            <div
              className="mt-3 px-3 py-2 rounded-[12px]"
              style={{
                background: "rgba(0,212,255,0.16)",
                border: "1px solid rgba(0,212,255,0.4)",
              }}
              data-testid="ai-focus-note"
            >
              <div
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 10,
                  letterSpacing: "0.12em",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "#7FE9FF",
                }}
              >
                AI Trainer focus
              </div>
              <div style={{ fontSize: 13, marginTop: 2, color: "#F4F3F1" }}>
                {adjustment.focus_reason || `Focus on ${adjustment.focus_exercise}`}
              </div>
            </div>
          )}

        {/* SET TRACKER */}
        <div style={{ marginTop: 22 }} data-testid="set-tracker">
          <div
            style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 11,
              letterSpacing: "0.18em",
              fontWeight: 700,
              textTransform: "uppercase",
              color: "rgba(244,243,241,0.55)",
              marginBottom: 10,
              paddingLeft: 4,
            }}
          >
            Set Tracker
          </div>

          <div
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 18,
              overflow: "hidden",
            }}
          >
            {/* Header row */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "44px 1fr 1fr 44px",
                gap: 8,
                padding: "12px 12px 8px",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "rgba(244,243,241,0.55)",
              }}
            >
              <div>Set</div>
              <div style={{ textAlign: "center" }}>Weight (kg)</div>
              <div style={{ textAlign: "center" }}>Reps</div>
              <div style={{ textAlign: "center" }}>✓</div>
            </div>

            {setLogs.map((row, i) => (
              <div
                key={i}
                data-testid={`set-row-${i}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "44px 1fr 1fr 44px",
                  gap: 8,
                  alignItems: "center",
                  padding: "10px 12px",
                  borderTop: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <div
                  style={{
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 800,
                    fontSize: 22,
                    color: "#F4F3F1",
                  }}
                >
                  {i + 1}
                </div>
                <Stepper
                  value={row.weight}
                  onDec={() => nudgeSet(i, "weight", -2.5)}
                  onInc={() => nudgeSet(i, "weight", 2.5)}
                  testid={`weight-${i}`}
                />
                <Stepper
                  value={row.reps}
                  onDec={() => nudgeSet(i, "reps", -1)}
                  onInc={() => nudgeSet(i, "reps", 1)}
                  testid={`reps-${i}`}
                />
                <button
                  onClick={() => toggleSetDone(i)}
                  data-testid={`check-${i}`}
                  aria-label={`Mark set ${i + 1} done`}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: "50%",
                    border: "2px solid rgba(255,255,255,0.35)",
                    background: row.done ? "#00D4FF" : "transparent",
                    borderColor: row.done ? "#00D4FF" : "rgba(255,255,255,0.35)",
                    display: "grid",
                    placeItems: "center",
                    cursor: "pointer",
                    justifySelf: "center",
                    transition: "all 160ms ease",
                  }}
                >
                  {row.done && <Check size={16} color="#0B0B0B" strokeWidth={3} />}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Previous / Next Exercise */}
        <div
          style={{
            marginTop: 22,
            display: "grid",
            gridTemplateColumns: "1fr 1.4fr",
            gap: 10,
          }}
        >
          <button
            onClick={goPrev}
            disabled={exIdx === 0}
            data-testid="prev-exercise"
            style={{
              height: 52,
              borderRadius: 100,
              background: "rgba(255,255,255,0.06)",
              border: 0,
              color: exIdx === 0 ? "rgba(244,243,241,0.35)" : "#F4F3F1",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 600,
              fontSize: 14,
              cursor: exIdx === 0 ? "not-allowed" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
            }}
          >
            <ChevronLeft size={16} /> Previous
          </button>
          <button
            onClick={goNextExercise}
            data-testid="next-exercise"
            style={{
              height: 52,
              borderRadius: 100,
              background: "#00D4FF",
              border: 0,
              color: "#0B0B0B",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontWeight: 700,
              fontSize: 15,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              boxShadow: "0 6px 18px rgba(0,212,255,0.35)",
            }}
          >
            {exIdx + 1 === exercises.length ? "Finish" : "Next exercise"} <ChevronRight size={16} />
          </button>
        </div>

        {/* AI ASSIST */}
        <div style={{ marginTop: 26 }} data-testid="ai-assist-section">
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 12,
              letterSpacing: "0.14em",
              fontWeight: 700,
              textTransform: "uppercase",
              color: "#00D4FF",
              marginBottom: 12,
            }}
          >
            <Bot size={14} /> AI Assist
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <ToggleChip
              icon={<Bot size={14} />}
              label="AI Trainer"
              on={aiTrainerOn}
              onToggle={() => {
                if (aiTrainerOn) {
                  setAiTrainerOn(false);
                  return;
                }
                let seen = false;
                try { seen = localStorage.getItem(GUIDE_FLAG) === "1"; } catch { /* ignore */ }
                if (!seen) setShowGuide(true);
                else setAiTrainerOn(true);
              }}
              testid="toggle-ai-trainer"
            />
            <ToggleChip
              label="Mirror"
              on={mirrorOn}
              onToggle={() => setMirrorOn((m) => !m)}
              testid="toggle-mirror"
            />
          </div>

          {!aiTrainerOn && (
            <div
              data-testid="ai-hint"
              style={{
                marginTop: 12,
                padding: "12px 14px",
                borderRadius: 14,
                background: "rgba(0,212,255,0.08)",
                border: "1px solid rgba(0,212,255,0.28)",
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
              }}
            >
              <Bot size={16} color="#00D4FF" style={{ marginTop: 2, flexShrink: 0 }} />
              <div
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  color: "rgba(244,243,241,0.85)",
                  lineHeight: 1.5,
                }}
              >
                Turn on AI Trainer to count your reps and check your form automatically.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Personal AI Trainer — full-screen camera surface */}
      {aiTrainerOn && (
        <PersonalAITrainer
          ref={patRef}
          exerciseName={current?.name || ""}
          active={aiTrainerOn && phase === "active" && !paused}
          mirror={mirrorOn}
          fullScreen
          onClose={() => setAiTrainerOn(false)}
          onSetEnd={() => { /* aggregated in completeSet via patRef.finaliseSet() */ }}
        />
      )}

      {/* First-time placement guide */}
      {showGuide && (
        <PlacementGuide
          onDone={() => {
            try { localStorage.setItem(GUIDE_FLAG, "1"); } catch { /* ignore */ }
            setShowGuide(false);
            setAiTrainerOn(true);
          }}
        />
      )}
    </div>
  );
}

function Stepper({ value, onDec, onInc, testid }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "rgba(255,255,255,0.06)",
        borderRadius: 100,
        padding: "4px 6px",
      }}
    >
      <button
        onClick={onDec}
        data-testid={`${testid}-dec`}
        aria-label="Decrease"
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          border: 0,
          color: "#F4F3F1",
          display: "grid",
          placeItems: "center",
          cursor: "pointer",
        }}
      >
        <Minus size={14} />
      </button>
      <div
        style={{
          fontFamily: "'Barlow Condensed', sans-serif",
          fontWeight: 700,
          fontSize: 20,
          color: "#F4F3F1",
        }}
      >
        {Number.isInteger(value) ? value : Number(value).toFixed(1)}
      </div>
      <button
        onClick={onInc}
        data-testid={`${testid}-inc`}
        aria-label="Increase"
        style={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          border: 0,
          color: "#F4F3F1",
          display: "grid",
          placeItems: "center",
          cursor: "pointer",
        }}
      >
        <Plus size={14} />
      </button>
    </div>
  );
}

function ToggleChip({ icon, label, on, onToggle, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={on}
      data-testid={testid}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 14px",
        borderRadius: 100,
        background: on ? "rgba(0,212,255,0.16)" : "rgba(255,255,255,0.05)",
        border: `1px solid ${on ? "rgba(0,212,255,0.5)" : "rgba(255,255,255,0.12)"}`,
        color: "#F4F3F1",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: 600,
        fontSize: 13,
        cursor: "pointer",
        transition: "background 160ms ease",
      }}
    >
      {icon}
      <span>{label}</span>
      <span
        style={{
          width: 26,
          height: 15,
          borderRadius: 100,
          background: on ? "#00D4FF" : "rgba(255,255,255,0.22)",
          position: "relative",
          transition: "background 180ms ease",
          marginLeft: 4,
        }}
      >
        <span
          style={{
            position: "absolute",
            top: 2,
            left: on ? 12 : 2,
            width: 11,
            height: 11,
            borderRadius: "50%",
            background: on ? "#0B0B0B" : "#fff",
            transition: "left 180ms ease",
          }}
        />
      </span>
    </button>
  );
}
